import { Expand, Leaf } from "lucide-react";
import {
  formatMoney,
  getCatalogItem,
  normalizeMoney,
  placementSizeFraction,
  plotRadius,
  PLOT_EXPANSION_PRICES,
  PLOT_MAX_TIER,
  PLOT_RADII
} from "@tribe/domain";
import { useVillageStore } from "./store";

/**
 * Arranging the yard.
 *
 * Replaces the old list of toggles, which put an item in the first free slot out of twenty
 * identical ones and gave you no say in where it went. Items now stand where you put them, face
 * the way you turn them, and come in four sizes down from the size they were drawn.
 *
 * This panel is only the inventory side of that: what is out, what is left to put out, and how
 * far the fence reaches. Picking something here hands it to the scene, where it is arranged
 * directly — see `PlacementEditBar`.
 */

function Price({ copper }: { copper: number }) {
  const money = normalizeMoney(copper);
  return (
    <span className="price" aria-label={formatMoney(copper)}>
      {money.gold > 0 && <span><i className="coin coin-gold" />{money.gold}</span>}
      {money.silver > 0 && <span><i className="coin coin-silver" />{money.silver}</span>}
      {(money.copper > 0 || copper === 0) && <span><i className="coin coin-copper" />{money.copper}</span>}
    </span>
  );
}

function ExpandPlot() {
  const property = useVillageStore((state) => state.data.property);
  const wallet = useVillageStore((state) => state.data.wallet);
  const expand = useVillageStore((state) => state.expandPlot);
  const nextTier = property.plotTier + 1;
  const maxed = nextTier > PLOT_MAX_TIER;
  const price = PLOT_EXPANSION_PRICES[nextTier] ?? 0;

  return (
    <section className="panel-section">
      <div className="section-heading">
        <h3>Your parcel</h3>
        <span>{plotRadius(property) * 2} voxels across</span>
      </div>
      {maxed ? (
        <p className="socket-note">Your parcel reaches as far as it can. There is room out here for anything.</p>
      ) : (
        <>
          <p className="socket-note">
            Push the fence out to {PLOT_RADII[nextTier]! * 2} voxels across. The biggest pieces need the room.
          </p>
          <button className="secondary-button full" disabled={wallet.copper < price} onClick={expand}>
            <Expand size={17} /> Expand for <Price copper={price} />
          </button>
        </>
      )}
    </section>
  );
}

export function YardEditor() {
  const data = useVillageStore((state) => state.data);
  const selectedPlacementId = useVillageStore((state) => state.selectedPlacementId);
  const selectPlacement = useVillageStore((state) => state.selectPlacement);
  const togglePlacement = useVillageStore((state) => state.togglePlacement);

  // The yard's half of the list. Placements cover both sides of the walls now, so without this
  // the bedroom's furniture would be listed as standing in the garden.
  const placed = data.property.placements.filter((placement) => placement.surface === "exterior");
  const placedItemIds = new Set(placed.map((placement) => placement.itemId));
  // Everything owned that can stand outdoors. No cap: with free-form placement there is no
  // reason to hide things somebody paid for.
  const available = data.catalog.filter(
    (item) => (item.category === "exterior" || item.category === "rare" || item.category === "plant") &&
      data.ownedItemIds.includes(item.id) && !placedItemIds.has(item.id)
  );

  return (
    <>
      <section className="panel-section first">
        <div className="section-heading">
          <h3>In your yard</h3>
          <span>{placed.length} placed</span>
        </div>

        {placed.length === 0 && <p className="socket-note">Nothing out here yet. Put something down from below.</p>}

        <div className="placed-list">
          {placed.map((placement) => {
            const item = getCatalogItem(placement.itemId);
            const selected = placement.id === selectedPlacementId;
            return (
              <button
                key={placement.id}
                className={selected ? "is-selected" : ""}
                aria-pressed={selected}
                onClick={() => selectPlacement(selected ? null : placement.id)}
              >
                <Leaf size={17} />
                <span>{item.name}</span>
                <small>{Math.round(placementSizeFraction(placement.scaleStep) * 100)}%</small>
              </button>
            );
          })}
        </div>

      </section>

      <section className="panel-section">
        <div className="section-heading">
          <h3>Ready to place</h3>
          <span>{available.length} in your collection</span>
        </div>
        <div className="socket-list">
          {available.map((item) => (
            <button key={item.id} onClick={() => togglePlacement(item.id)}>
              <Leaf size={17} /><span>{item.name}</span><small>Place</small>
            </button>
          ))}
        </div>
        {available.length === 0 && <p className="socket-note">Everything you own is already out in the yard.</p>}
      </section>

      <ExpandPlot />
    </>
  );
}
